import { jsPDF } from 'jspdf';

export interface DashboardPdfFilter {
  label: string;
  value: string;
}

export interface DashboardPdfSlice {
  color: string;
  count: number;
  percentage: number;
}

interface DashboardPdfInput {
  title?: string;
  subtitle?: string;
  filters: DashboardPdfFilter[];
  dateFrom: string;
  dateTo: string;
  slices: DashboardPdfSlice[];
}

const COLOR_META: Record<string, { label: string; rgb: [number, number, number]; stroke?: [number, number, number] }> = {
  red: { label: 'Red', rgb: [239, 68, 68] },
  orange: { label: 'Orange', rgb: [245, 158, 11] },
  yellow: { label: 'Yellow', rgb: [253, 224, 71] },
  green: { label: 'Green', rgb: [34, 197, 94] },
  blue: { label: 'Blue', rgb: [37, 99, 235] },
  na: { label: 'N/A', rgb: [156, 163, 175] },
  none: { label: 'No Selection', rgb: [255, 255, 255], stroke: [17, 24, 39] }
};

type PdfWithFontApi = jsPDF & {
  addFileToVFS: (fileName: string, fontName: string) => void;
  addFont: (fileName: string, fontName: string, fontStyle: string) => void;
};

const sanitizePdfText = (input: string) => String(input || '')
  .replace(/[“”]/g, '"')
  .replace(/[‘’]/g, "'")
  .replace(/[–—]/g, '-')
  .replace(/\u2026/g, '...')
  .replace(/[^\x20-\x7E\n\t]/g, ' ')
  .replace(/[ \t]+\n/g, '\n')
  .trim();

const toLines = (value: string | string[]) => (Array.isArray(value) ? value : [String(value)]);

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const loadFontBase64 = (fontPath: string) => fetch(fontPath)
  .then(async (response) => {
    if (!response.ok) {
      throw new Error(`Failed to load font file: ${fontPath}`);
    }
    return arrayBufferToBase64(await response.arrayBuffer());
  });

let ralewayRegularBase64Promise: Promise<string> | null = null;
let ralewayBoldBase64Promise: Promise<string> | null = null;

const ensureRalewayFontRegistered = async (pdf: jsPDF) => {
  if (!ralewayRegularBase64Promise) {
    ralewayRegularBase64Promise = loadFontBase64('/fonts/Raleway-Regular.ttf');
  }
  if (!ralewayBoldBase64Promise) {
    ralewayBoldBase64Promise = loadFontBase64('/fonts/Raleway-SemiBold.ttf');
  }
  const [regularBase64, boldBase64] = await Promise.all([ralewayRegularBase64Promise, ralewayBoldBase64Promise]);
  const pdfApi = pdf as PdfWithFontApi;
  pdfApi.addFileToVFS('Raleway-Regular.ttf', regularBase64);
  pdfApi.addFont('Raleway-Regular.ttf', 'Raleway', 'normal');
  pdfApi.addFileToVFS('Raleway-SemiBold.ttf', boldBase64);
  pdfApi.addFont('Raleway-SemiBold.ttf', 'Raleway', 'bold');
};

const loadLogoDataUrl = async () => {
  try {
    const response = await fetch('/icon.png');
    if (!response.ok) return '';
    const blob = await response.blob();
    const base64 = arrayBufferToBase64(await blob.arrayBuffer());
    const type = blob.type || 'image/png';
    return `data:${type};base64,${base64}`;
  } catch {
    return '';
  }
};

const describeSlice = (color: string) => {
  const known = COLOR_META[color];
  if (known) return known;
  const fallback = sanitizePdfText(color || 'Unknown');
  const label = fallback ? `${fallback.charAt(0).toUpperCase()}${fallback.slice(1)}` : 'Unknown';
  return { label, rgb: [156, 163, 175] as [number, number, number] };
};

const formatPostCount = (count: number) => {
  const safeCount = Number.isFinite(count) ? Math.max(0, Math.round(count)) : 0;
  const formatted = safeCount.toLocaleString('en-US');
  return `${formatted} ${safeCount === 1 ? 'post' : 'posts'}`;
};

export const formatDashboardPdfDate = (value: string) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return 'n/a';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return sanitizePdfText(trimmed) || 'n/a';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return 'n/a';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date);
};

export const joinDashboardFilterValue = (labels: string[]) => {
  const cleaned = labels
    .map((label) => sanitizePdfText(label))
    .filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(', ') : 'all';
};

const drawWedge = (
  pdf: jsPDF,
  cx: number,
  cy: number,
  radius: number,
  startDeg: number,
  endDeg: number,
  rgb: [number, number, number],
  stroke: [number, number, number] = [255, 255, 255]
) => {
  const sweep = endDeg - startDeg;
  pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
  pdf.setDrawColor(stroke[0], stroke[1], stroke[2]);
  if (sweep >= 359.5) {
    pdf.setLineWidth(1.5);
    pdf.circle(cx, cy, radius, 'FD');
    return;
  }
  const steps = Math.max(18, Math.ceil(Math.abs(sweep) / 3));
  pdf.setLineWidth(2.25);
  pdf.setLineJoin('round');
  pdf.moveTo(cx, cy);
  for (let step = 0; step <= steps; step += 1) {
    const deg = startDeg + (sweep * step) / steps;
    const rad = (deg * Math.PI) / 180;
    pdf.lineTo(cx + radius * Math.cos(rad), cy + radius * Math.sin(rad));
  }
  pdf.close();
  pdf.fillStroke();
};

export const createDashboardSummaryPdf = async ({
  title,
  subtitle,
  filters,
  dateFrom,
  dateTo,
  slices
}: DashboardPdfInput) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4'
  });

  let fontFamily: 'Raleway' | 'helvetica' = 'helvetica';
  try {
    await ensureRalewayFontRegistered(pdf);
    pdf.setFont('Raleway', 'normal');
    pdf.setFontSize(11);
    pdf.getTextWidth('Traffic Lite PDF');
    fontFamily = 'Raleway';
  } catch (error) {
    console.warn('Falling back to helvetica for dashboard PDF export:', error);
    fontFamily = 'helvetica';
  }

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const marginX = 56;
  const bottomLimit = pageHeight - 46;
  const contentWidth = pageWidth - marginX * 2;
  let cursorY = 48;

  const logoDataUrl = await loadLogoDataUrl();
  const safeTitle = sanitizePdfText(title || '');
  const safeSubtitle = sanitizePdfText(subtitle || '');
  const heading = safeTitle || 'Data Dashboard';
  const chartSlices = slices.filter((slice) => slice.count > 0);
  const totalPosts = chartSlices.reduce((sum, slice) => sum + slice.count, 0);

  const ensureSpace = (height: number) => {
    if (cursorY + height <= bottomLimit) return;
    pdf.addPage();
    cursorY = 48;
  };

  const drawWrappedField = (label: string, value: string, x: number, width: number) => {
    const labelText = `${label}: `;
    const lineHeight = 16;
    pdf.setFont(fontFamily, 'bold');
    pdf.setFontSize(12);
    const labelWidth = Math.min(pdf.getTextWidth(labelText), width * 0.46);
    pdf.setFont(fontFamily, 'normal');
    const valueWidth = Math.max(80, width - labelWidth);
    const valueLines = toLines(pdf.splitTextToSize(sanitizePdfText(value) || 'all', valueWidth));
    ensureSpace(valueLines.length * lineHeight);
    valueLines.forEach((line, index) => {
      const lineY = cursorY + index * lineHeight;
      if (index === 0) {
        pdf.setFont(fontFamily, 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(31, 41, 55);
        pdf.text(labelText, x, lineY);
      }
      pdf.setFont(fontFamily, 'normal');
      pdf.setFontSize(12);
      pdf.setTextColor(17, 24, 39);
      pdf.text(line, x + labelWidth, lineY);
    });
    cursorY += valueLines.length * lineHeight;
  };

  pdf.setFont(fontFamily, 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(17, 24, 39);
  const brandText = 'Traffic Lite';
  const logoSize = 18;
  const brandGap = 8;
  const brandTextWidth = pdf.getTextWidth(brandText);
  const brandWidth = (logoDataUrl ? logoSize + brandGap : 0) + brandTextWidth;
  let brandX = (pageWidth - brandWidth) / 2;
  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, 'PNG', brandX, cursorY - 13, logoSize, logoSize, undefined, 'FAST');
    brandX += logoSize + brandGap;
  }
  pdf.text(brandText, brandX, cursorY);
  cursorY += 28;

  pdf.setFont(fontFamily, 'bold');
  pdf.setFontSize(safeTitle ? 20 : 18);
  pdf.setTextColor(17, 24, 39);
  const headingLines = toLines(pdf.splitTextToSize(heading, contentWidth));
  pdf.text(headingLines, pageWidth / 2, cursorY, { align: 'center' });
  cursorY += headingLines.length * (safeTitle ? 24 : 22);

  if (safeSubtitle) {
    cursorY += 2;
    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(12);
    pdf.setTextColor(75, 85, 99);
    const subtitleLines = toLines(pdf.splitTextToSize(safeSubtitle, contentWidth));
    pdf.text(subtitleLines, pageWidth / 2, cursorY, { align: 'center' });
    cursorY += subtitleLines.length * 16;
  }

  cursorY += 22;
  filters.forEach((filter) => {
    drawWrappedField(filter.label, filter.value, marginX, contentWidth);
  });

  cursorY += 6;
  ensureSpace(18);
  pdf.setFont(fontFamily, 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(31, 41, 55);
  pdf.text('Date Range:', marginX, cursorY);
  cursorY += 18;
  drawWrappedField('From', dateFrom || 'n/a', marginX + 16, contentWidth - 16);
  drawWrappedField('To', dateTo || 'n/a', marginX + 16, contentWidth - 16);

  const cardWidth = Math.min(440, contentWidth);
  const pieRadius = chartSlices.length > 0 ? 92 : 0;
  const legendRowHeight = 20;
  const legendHeight = chartSlices.length > 0 ? chartSlices.length * legendRowHeight : 28;
  const cardPadTop = 22;
  const cardPadBottom = 20;
  const cardTitleHeight = 22;
  const pieGap = chartSlices.length > 0 ? 18 : 8;
  const totalGap = 12;
  const totalHeight = 18;
  const cardHeight = cardPadTop
    + cardTitleHeight
    + pieGap
    + pieRadius * 2
    + (chartSlices.length > 0 ? 16 : 0)
    + legendHeight
    + totalGap
    + totalHeight
    + cardPadBottom;

  cursorY += 8;
  let cardTop = cursorY + 16;
  const pieCenterOffset = cardPadTop + cardTitleHeight + pieGap + pieRadius;
  const idealCardTop = chartSlices.length > 0
    ? pageHeight * 0.56 - pieCenterOffset
    : cardTop;
  const maxCardTop = bottomLimit - cardHeight;
  if (cardTop + cardHeight > bottomLimit) {
    pdf.addPage();
    cardTop = 56;
  } else if (idealCardTop > cardTop && idealCardTop <= maxCardTop) {
    cardTop = idealCardTop;
  }

  const cardX = (pageWidth - cardWidth) / 2;
  pdf.setFillColor(248, 249, 250);
  pdf.setDrawColor(222, 226, 230);
  pdf.setLineWidth(1);
  pdf.roundedRect(cardX, cardTop, cardWidth, cardHeight, 12, 12, 'FD');

  let cardCursor = cardTop + cardPadTop + 12;
  pdf.setFont(fontFamily, 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(33, 37, 41);
  pdf.text('AI-Usage Distribution', pageWidth / 2, cardCursor, { align: 'center' });
  cardCursor += cardTitleHeight + 4;

  if (chartSlices.length === 0) {
    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(12);
    pdf.setTextColor(107, 114, 128);
    pdf.text('No posts match the current view.', pageWidth / 2, cardCursor + 8, { align: 'center' });
    cardCursor += legendHeight;
  } else {
    const pieCenterY = cardCursor + pieRadius;
    let angle = -90;
    chartSlices.forEach((slice) => {
      const sweep = totalPosts > 0 ? (slice.count / totalPosts) * 360 : 0;
      const meta = describeSlice(slice.color);
      drawWedge(pdf, pageWidth / 2, pieCenterY, pieRadius, angle, angle + sweep, meta.rgb, meta.stroke);
      angle += sweep;
    });
    cardCursor += pieRadius * 2 + 18;

    pdf.setFontSize(11);
    const legendRows = chartSlices.map((slice) => {
      const meta = describeSlice(slice.color);
      return {
        ...meta,
        leftText: `${meta.label}: ${slice.percentage}%`,
        rightText: `(${formatPostCount(slice.count)})`
      };
    });
    const maxLeft = Math.max(...legendRows.map((row) => {
      pdf.setFont(fontFamily, 'normal');
      return pdf.getTextWidth(row.leftText);
    }));
    const maxRight = Math.max(...legendRows.map((row) => {
      pdf.setFont(fontFamily, 'normal');
      return pdf.getTextWidth(row.rightText);
    }));
    const swatchSize = 11;
    const swatchGap = 8;
    const columnGap = 18;
    const legendWidth = swatchSize + swatchGap + maxLeft + columnGap + maxRight;
    const legendX = (pageWidth - legendWidth) / 2;

    legendRows.forEach((row) => {
      const swatchY = cardCursor - 9;
      pdf.setFillColor(row.rgb[0], row.rgb[1], row.rgb[2]);
      const swatchStroke = row.stroke || [209, 213, 219];
      pdf.setDrawColor(swatchStroke[0], swatchStroke[1], swatchStroke[2]);
      pdf.setLineWidth(0.6);
      pdf.roundedRect(legendX, swatchY, swatchSize, swatchSize, 2, 2, 'FD');
      pdf.setFont(fontFamily, 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(55, 65, 81);
      pdf.text(row.leftText, legendX + swatchSize + swatchGap, cardCursor);
      pdf.setTextColor(75, 85, 99);
      pdf.text(row.rightText, legendX + swatchSize + swatchGap + maxLeft + columnGap, cardCursor);
      cardCursor += legendRowHeight;
    });
  }

  cardCursor += 4;
  pdf.setFont(fontFamily, 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(17, 24, 39);
  pdf.text(`Total: ${formatPostCount(totalPosts)}`, pageWidth / 2, cardCursor, { align: 'center' });

  const pageCount = pdf.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    pdf.setPage(pageNumber);
    pdf.setFont(fontFamily, 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(156, 163, 175);
    const footer = pageCount > 1 ? `Traffic Lite  ·  ${pageNumber}` : 'Traffic Lite';
    pdf.text(footer, pageWidth / 2, pageHeight - 24, { align: 'center' });
  }

  return pdf;
};

export const exportDashboardSummaryToPdf = async (input: DashboardPdfInput) => {
  const pdf = await createDashboardSummaryPdf(input);
  const timeStamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  pdf.save(`traffic-lite-dashboard-summary-${timeStamp}.pdf`);
};
